/**
 * The provider-feedback comment sidebar (BLA-70 / B1; composer UX + ordering
 * per BLA-317 / ADR-072; Google Docs-style card presentation & margin alignment).
 *
 * A presentational, prop-driven panel: it lists a document's comments and hosts
 * a composer when text is selected in the document canvas. It owns only
 * local draft/edit state; all persistence flows through the callbacks (wired to
 * `useComments`), so it unit-tests cleanly under jsdom while the live-editor
 * decoration + span selection are guarded by Playwright.
 *
 * Google Docs card styling:
 * - Author avatar circle with initials
 * - Author name and formatted timestamp
 * - Edit and delete actions
 * - Quoted text excerpt banner if anchored
 * - Comment body
 * - Vertical positioning map support (`positions` map) to position cards beside
 *   their anchored highlights in the document canvas with collision avoidance,
 *   including selection-aligned pending composer positioning (`pendingTop` / `__pending__`).
 */
import { useEffect, useMemo, useRef, useState } from "react";

import type { CommentAnchor, DocumentComment } from "../../types";
import { getInitialsFromEmail } from "../../utils/initials";
import { formatCommentTimestamp } from "./commentPositioning";

/** Props for {@link CommentSidebar}. */
export interface CommentSidebarProps {
  /** The document's comments, in creation order. */
  comments: DocumentComment[];
  /** True while the initial load is in flight. */
  loading?: boolean;
  /** A plain-language load error, or null. */
  error?: string | null;
  /** Comment ids whose anchored text can no longer be located in the document. */
  staleIds?: Set<string>;
  /** The currently highlighted comment id, if any. */
  activeCommentId?: string | null;
  /** The comment id whose document anchor is currently hovered (BLA-318). */
  hoveredCommentId?: string | null;
  /** Called when a comment row is clicked (to scroll/flash its highlight). */
  onSelect?: (id: string) => void;
  /**
   * Called when a comment row is hovered (or unhovered, with `null`) so its
   * anchor can be distinguished in the document — the mirror of hovering the
   * anchor itself. Additive to `onSelect`: click keeps working unchanged, and
   * hover is simply absent on touch, where this never fires.
   */
  onHover?: (id: string | null) => void;
  /** Create a comment from the composer (anchored to any pending selection). */
  onCreate: (body: string) => Promise<void> | void;
  /** Save an edit to a comment's body. */
  onUpdate: (id: string, body: string) => Promise<void> | void;
  /** Delete a comment. */
  onDelete: (id: string) => void;
  /** The staged span anchor (if any), whose quoted text is shown above the composer. */
  pendingAnchor?: CommentAnchor | null;
  /** Discard the staged span anchor (the next comment becomes document-level). */
  onClearPending?: () => void;
  /** Initial top coordinate in pixels for pending comment positioning. */
  pendingTop?: number | null;
  /** Map of comment ID to vertical pixel top offset for inline margin alignment. */
  positions?: Map<string, number>;
  /** Optional class name for the root container. */
  className?: string;
}

/** One Google Docs-styled editable comment card. */
export function CommentItem({
  comment,
  stale,
  active,
  hovered,
  topOffset,
  onSelect,
  onHover,
  onUpdate,
  onDelete,
}: {
  comment: DocumentComment;
  stale: boolean;
  active: boolean;
  hovered: boolean;
  topOffset?: number;
  onSelect?: (id: string) => void;
  onHover?: (id: string | null) => void;
  onUpdate: (id: string, body: string) => Promise<void> | void;
  onDelete: (id: string) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(comment.body);

  const startEdit = (): void => {
    setBody(comment.body);
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    try {
      await onUpdate(comment.id, body);
      setEditing(false);
    } catch {
      // Failed, keep editing state
    }
  };

  const authorDisplay = comment.author_display || comment.author || "User";
  const authorAccount = comment.author?.includes("@")
    ? comment.author
    : comment.author_display?.includes("@")
      ? comment.author_display
      : null;
  const authorInitials = authorAccount
    ? getInitialsFromEmail(authorAccount)
    : "U";
  const timestamp = formatCommentTimestamp(comment.created_at);

  const style: React.CSSProperties =
    topOffset !== undefined
      ? {
          position: "absolute",
          top: `${topOffset}px`,
          left: "0",
          right: "0",
        }
      : {};

  if (editing) {
    return (
      <li
        style={style}
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-md transition-all"
        data-testid={`comment-item-${comment.id}`}
      >
        <textarea
          value={body}
          aria-label="Edit comment"
          data-testid={`comment-edit-body-${comment.id}`}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none"
        />
        <div className="mt-2.5 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid={`comment-cancel-${comment.id}`}
            onClick={() => setEditing(false)}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={body.trim() === ""}
            data-testid={`comment-save-${comment.id}`}
            onClick={save}
            className="rounded-md bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-40"
          >
            Save
          </button>
        </div>
      </li>
    );
  }

  const borderClass = active
    ? "border-indigo-400 shadow-md ring-2 ring-indigo-100 bg-white"
    : hovered
      ? "border-indigo-300 shadow-sm bg-white"
      : "border-slate-200 shadow-xs bg-white hover:border-slate-300 hover:shadow-sm";

  return (
    <li
      style={style}
      data-testid={`comment-item-${comment.id}`}
      role="article"
      aria-label={`Comment by ${authorDisplay}`}
      className={`rounded-xl border p-4 transition-all ${borderClass}`}
      onMouseEnter={() => onHover?.(comment.id)}
      onMouseLeave={() => onHover?.(null)}
    >
      {/* Header: Avatar, Author, Time, Resolve & Menu */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            aria-hidden="true"
            className="w-8 h-8 rounded-full bg-slate-700 text-white flex items-center justify-center font-bold text-xs shrink-0"
          >
            {authorInitials}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-slate-800 truncate">
              {authorDisplay}
            </span>
            {timestamp && (
              <span className="text-[11px] text-slate-500">{timestamp}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0 relative">
          {stale && (
            <span
              data-testid={`comment-stale-${comment.id}`}
              className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
            >
              text changed
            </span>
          )}

          {/* Edit action */}
          <button
            type="button"
            data-testid={`comment-edit-${comment.id}`}
            onClick={startEdit}
            className="p-1 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100 transition-colors"
            title="Edit"
            aria-label="Edit comment"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>

          {/* Delete action */}
          <button
            type="button"
            data-testid={`comment-delete-${comment.id}`}
            onClick={() => onDelete(comment.id)}
            className="p-1 text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors"
            title="Delete"
            aria-label="Delete comment"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Body & Quoted Context */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect?.(comment.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect?.(comment.id);
          }
        }}
        data-testid={`comment-select-${comment.id}`}
        className="mt-2.5 block w-full cursor-pointer text-left focus:outline-none"
      >
        {comment.anchor?.quoted_text && (
          <div className="mb-2 border-l-2 border-amber-400 bg-amber-50/60 px-2 py-1 text-xs italic text-slate-600 rounded-r">
            “{comment.anchor.quoted_text}”
          </div>
        )}
        <p className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed">
          {comment.body}
        </p>
      </div>
    </li>
  );
}

/**
 * Render the comment sidebar (composer + list) with Google Docs-style cards
 * and margin positioning support.
 *
 * @param props - The comments, load flags, and persistence callbacks.
 * @returns The sidebar element.
 */
export function CommentSidebar({
  comments,
  loading = false,
  error = null,
  staleIds,
  activeCommentId = null,
  hoveredCommentId = null,
  onSelect,
  onHover,
  onCreate,
  onUpdate,
  onDelete,
  pendingAnchor = null,
  onClearPending,
  pendingTop = null,
  positions,
  className = "",
}: CommentSidebarProps): React.ReactElement {
  const [body, setBody] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLElement>(null);

  // Starting to comment on a selection (toolbar / right-click / keyboard
  // shortcut) stages a pending anchor — that is the "start adding a comment"
  // moment for the anchored flow. Bring the composer into view and put the
  // cursor in it so the provider never has to hunt for it (BLA-317).
  useEffect(() => {
    if (pendingAnchor?.quoted_text == null || pendingAnchor.quoted_text === "")
      return;
    composerRef.current?.scrollIntoView?.({ block: "nearest" });
    bodyRef.current?.focus();
  }, [pendingAnchor]);

  const submit = async (): Promise<void> => {
    try {
      await onCreate(body);
      setBody("");
    } catch {
      // Failed, keep editing state
    }
  };

  const orderedComments = comments.slice().sort((a, b) => {
    if (positions) {
      const topA = positions.get(a.id) ?? 0;
      const topB = positions.get(b.id) ?? 0;
      if (topA !== topB) return topA - topB;
    }
    return b.created_at.localeCompare(a.created_at);
  });

  const composerTop =
    positions?.get("__pending__") ??
    (pendingTop !== null && pendingTop !== undefined ? pendingTop : undefined);

  const hasPending =
    pendingAnchor?.quoted_text != null && pendingAnchor.quoted_text !== "";

  const isPositioned =
    (positions !== undefined && positions.size > 0) ||
    composerTop !== undefined;

  const maxBottom = useMemo(() => {
    let max = 0;
    if (positions && positions.size > 0) {
      for (const [id, top] of positions.entries()) {
        if (id === "__pending__") continue;
        const bottom = top + 180;
        if (bottom > max) max = bottom;
      }
    }
    if (composerTop !== undefined) {
      const composerBottom = composerTop + 220;
      if (composerBottom > max) max = composerBottom;
    }
    return max;
  }, [positions, composerTop]);

  const listMinHeight = isPositioned ? Math.max(400, maxBottom) : undefined;

  const composerStyle: React.CSSProperties =
    composerTop !== undefined
      ? {
          position: "absolute",
          top: `${composerTop}px`,
          left: "0",
          right: "0",
        }
      : {};

  return (
    <section
      aria-label="Comments"
      data-testid="comment-sidebar"
      className={`space-y-4 ${className}`.trim()}
    >
      {loading && <p className="text-xs text-slate-500">Loading comments…</p>}
      {error != null && <p className="text-xs text-rose-600">{error}</p>}
      {!loading && error == null && comments.length === 0 && !hasPending && (
        <p className="text-xs text-slate-400">No comments yet.</p>
      )}
      <ul
        data-testid="comment-list"
        style={listMinHeight ? { minHeight: `${listMinHeight}px` } : undefined}
        className={`relative space-y-3 ${isPositioned ? "min-h-[400px]" : ""}`}
      >
        {hasPending && (
          <li
            ref={composerRef as React.RefObject<HTMLLIElement>}
            style={composerStyle}
            data-testid="comment-composer-card"
            className={`z-10 space-y-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3.5 shadow-md ${
              composerTop === undefined ? "sticky top-2" : ""
            }`}
          >
            <div
              data-testid="comment-pending-anchor"
              className="flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-100/70 px-2.5 py-1 text-xs text-amber-900"
            >
              <span className="truncate">
                Commenting on:{" "}
                <span className="italic">“{pendingAnchor.quoted_text}”</span>
              </span>
              <button
                type="button"
                aria-label="Clear selection"
                data-testid="comment-clear-pending"
                onClick={() => onClearPending?.()}
                className="font-bold text-amber-600 hover:text-amber-800"
              >
                ×
              </button>
            </div>
            <textarea
              ref={bodyRef}
              value={body}
              aria-label="New comment"
              placeholder="Add a comment…"
              data-testid="comment-composer-body"
              onChange={(e) => setBody(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-sm focus:border-blue-500 focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="comment-cancel"
                onClick={() => onClearPending?.()}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200/60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={body.trim() === ""}
                data-testid="comment-add"
                onClick={submit}
                className="rounded-md bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-40"
              >
                Comment
              </button>
            </div>
          </li>
        )}
        {orderedComments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            stale={staleIds?.has(comment.id) ?? false}
            active={comment.id === activeCommentId}
            hovered={comment.id === hoveredCommentId}
            topOffset={positions?.get(comment.id)}
            onSelect={onSelect}
            onHover={onHover}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </section>
  );
}

export default CommentSidebar;
